import React from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';

export interface DropdownOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface DropdownPanelProps {
  isOpen: boolean;
  options: DropdownOption[];
  value: string | number | (string | number)[];
  activeIndex: number | null;
  onOptionClick: (value: string | number) => void;
  onOptionMouseEnter: (index: number) => void;
  listboxId: string;
  labelId?: string;
  uniqueIdPrefix: string;
  className?: string;
  optionClassName?: string;
  popperStyles: React.CSSProperties;
  popperAttributes: { [key: string]: string } | undefined;
  isMultiSelect?: boolean;
  portalContainer?: HTMLElement | null;
}

export const DropdownPanel = React.forwardRef<HTMLDivElement, DropdownPanelProps>(({
  isOpen,
  options,
  value,
  activeIndex,
  onOptionClick,
  onOptionMouseEnter,
  listboxId,
  labelId,
  uniqueIdPrefix,
  className,
  optionClassName,
  popperStyles,
  popperAttributes,
  isMultiSelect = false,
  portalContainer,
}, ref) => {
  if (typeof document === 'undefined') {
    return null;
  }

  const isSelected = (optionValue: string | number) => {
    if (isMultiSelect) {
      return Array.isArray(value) && value.includes(optionValue);
    }
    return value === optionValue;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.12, when: 'beforeChildren' } },
  };
  const panelVariants = {
    hidden: { y: -5, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.12, ease: 'easeOut' } },
  };

  const dropdownContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={ref}
          id={listboxId}
          role="listbox"
          aria-multiselectable={isMultiSelect}
          aria-labelledby={labelId}
          tabIndex={-1}
          style={popperStyles}
          {...popperAttributes}
          className="z-[1001]"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <motion.div
            className={clsx(
              'bg-white border-2 border-black rounded-lg shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]',
              'max-h-60 overflow-y-auto focus:outline-none py-1',
              '[.theme-liquid-glass_&]:bg-slate-800/90 [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20',
              '[.theme-liquid-glass_&]:rounded-xl [.theme-liquid-glass_&]:backdrop-blur-xl',
              '[.theme-liquid-glass_&]:shadow-[0_16px_40px_rgba(0,0,0,0.35)]',
              className
            )}
            variants={panelVariants}
          >
            {options.length > 0 ? (
              options.map((option, index) => {
                const selected = isSelected(option.value);
                return (
                  <div
                    key={option.value}
                    id={`${uniqueIdPrefix}-option-${index}`}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={option.disabled}
                    onClick={() => !option.disabled && onOptionClick(option.value)}
                    onMouseEnter={() => !option.disabled && onOptionMouseEnter(index)}
                    className={clsx(
                      'px-3.5 py-2 text-slate-800 font-medium cursor-pointer flex justify-between items-center',
                      '[.theme-liquid-glass_&]:text-white/85',
                      option.disabled
                        ? 'opacity-50 cursor-not-allowed text-slate-500 [.theme-liquid-glass_&]:text-white/40'
                        : 'hover:bg-slate-100 [.theme-liquid-glass_&]:hover:bg-white/10',
                      activeIndex === index && !option.disabled && 'bg-slate-100 [.theme-liquid-glass_&]:bg-white/10',
                      selected && !option.disabled && 'bg-emerald-50 text-emerald-700 [.theme-liquid-glass_&]:bg-violet-500/20 [.theme-liquid-glass_&]:text-white',
                      optionClassName
                    )}
                  >
                    {option.label}
                    {selected && !option.disabled && (
                      <Check size={16} className="text-emerald-600 [.theme-liquid-glass_&]:text-violet-300" />
                    )}
                  </div>
                );
              })
            ) : (
              <div className="px-3.5 py-2 text-slate-500 italic [.theme-liquid-glass_&]:text-white/60">No options available</div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(dropdownContent, portalContainer || document.body);
});

DropdownPanel.displayName = 'DropdownPanel';
